import React from 'react';
import { observer } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';

import { FormSubmitEvent } from 'ui/base/types';
import { Theme, useStyles } from 'ui/base/theme';
import { ActiveButton } from 'ui/public/button';
import { PartitionedPage } from 'ui/add_pangolin/partitioned_page/partitioned_page';

import { ResultKind } from 'services/base/types';
import { BasePath, pagePathToPathString } from 'services/base/page_path';

import { IAccountsDomain, Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import baseStyles from './add_pangolin_profile.module.css';
import Messages from './add_pangolin_profile_messages';
import { AddPangolinProfileStore } from './add_pangolin_profile_store';
import { ChoiceStore } from 'ui/base/input';
import { DeploymentProfile, getDeploymentProfileDetails, getDeploymentProfileGraphCoverage, getDeploymentProfileGraphImage, getDeploymentProfileGuide, getDeploymentProfileIconImage, getDeploymentProfileName, getDeploymentProfileSummary, getDeploymentProfileWarning, themedDeploymentProfileGraphImageStyles, themedDeploymentProfileIconImageStyles } from 'assets/enums/deployment_profiles/deployment_profiles';
import classNames from 'classnames';
import { RadioInput } from 'ui/standard/input/radio_input';
import ReactMarkdown from 'react-markdown';

type AddPangolinProfileStyles = {
  title: string;
  instructions: string;
  action: string;

  content: string;

  section: string;

  field: string;

  detail: string;

  clickable: string;

  cardContainer: string;
  cardDefault: string;
  cardDisabled: string;
  cardSelected: string;
  cardIcon: string;
  cardText: string;
  cardName: string;
  cardInfo: string;
  cardTags: string;

  graphContainer: string;
  graphNotes: string;
  graphWarning: string;
  graphFlowsTable: string;
  graphFlowLine: string;
  graphFlowDisabled: string;
};

const themedStyles: Record<Theme, AddPangolinProfileStyles> = {
  [Theme.DAYLIGHT]: {
    title: baseStyles.daylightTitle,
    instructions: baseStyles.daylightInstructions,
    action: baseStyles.action,

    content: baseStyles.content,

    section: baseStyles.daylightSection,

    field: baseStyles.field,

    detail: baseStyles.daylightDetail,

    clickable: baseStyles.clickable,

    cardContainer: baseStyles.cardContainer,
    cardDefault: baseStyles.daylightCardDefault,
    cardDisabled: baseStyles.daylightCardDisabled,
    cardSelected: baseStyles.daylightCardSelected,
    cardIcon: baseStyles.cardIcon,
    cardText: baseStyles.cardText,
    cardName: baseStyles.cardName,
    cardInfo: baseStyles.cardInfo,
    cardTags: baseStyles.daylightCardTags,

    graphContainer: baseStyles.graphContainer,
    graphNotes: baseStyles.graphNotes,
    graphWarning: baseStyles.daylightGraphWarning,
    graphFlowsTable: baseStyles.daylightGraphFlowsTable,
    graphFlowLine: baseStyles.graphFlowLine,
    graphFlowDisabled: baseStyles.graphFlowDisabled,
  },
  [Theme.MIDNIGHT]: {
    title: baseStyles.midnightTitle,
    instructions: baseStyles.midnightInstructions,
    action: baseStyles.action,

    content: baseStyles.content,

    section: baseStyles.midnightSection,

    field: baseStyles.field,

    detail: baseStyles.midnightDetail,

    clickable: baseStyles.clickable,

    cardContainer: baseStyles.cardContainer,
    cardDefault: baseStyles.midnightCardDefault,
    cardDisabled: baseStyles.midnightCardDisabled,
    cardSelected: baseStyles.midnightCardSelected,
    cardIcon: baseStyles.cardIcon,
    cardText: baseStyles.cardText,
    cardName: baseStyles.cardName,
    cardInfo: baseStyles.cardInfo,
    cardTags: baseStyles.midnightCardTags,

    graphContainer: baseStyles.graphContainer,
    graphNotes: baseStyles.graphNotes,
    graphWarning: baseStyles.midnightGraphWarning,
    graphFlowsTable: baseStyles.midnightGraphFlowsTable,
    graphFlowLine: baseStyles.graphFlowLine,
    graphFlowDisabled: baseStyles.graphFlowDisabled,
  },
};

type ProfileSelectInputProps = {
  styles: AddPangolinProfileStyles;
  choices: ChoiceStore<DeploymentProfile>;
  choice: DeploymentProfile;
};
const ProfileSelectInput = observer(({ styles, choices, choice }: ProfileSelectInputProps) => {
  const selected = choice === choices.selected;

  const profileIconImages = useStyles(themedDeploymentProfileIconImageStyles);
  const profileIcon = getDeploymentProfileIconImage(profileIconImages, choice);

  const profileName = getDeploymentProfileName(choice);
  const profileSummary = getDeploymentProfileSummary(choice);

  const onSelect = React.useCallback(() => {
    choices.setSelected(choice);
  }, [choices, choice]);

  return (
    <div onClick={onSelect}
         className={classNames(styles.clickable, styles.field,
                               selected ? styles.cardSelected : styles.cardDefault)}>
      <img className={styles.cardIcon} src={profileIcon} alt='Package Icon' />
      <div className={styles.cardText}>
        <div className={styles.cardName}>
          {profileName}
        </div>
        <div className={styles.cardInfo}>
          {profileSummary}
        </div>
      </div>
      <RadioInput
        store={choices}
        choice={choice}
      />
    </div>
  );
});

type BaseAddPangolinProfileProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
};

export const BaseAddPangolinProfile = observer(({ accounts, pangolin }: BaseAddPangolinProfileProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const [store] = React.useState(new AddPangolinProfileStore());

  const profile = store.profileChoices.value;
  const profileDetails = getDeploymentProfileDetails(profile);
  const profileGraphImages = useStyles(themedDeploymentProfileGraphImageStyles);
  const profileGraphImage = profile ? getDeploymentProfileGraphImage(profileGraphImages, profile) : undefined;
  const profileGraphGuide = profile ? getDeploymentProfileGuide(profile) : undefined;
  const profileGraphCoverage = profile ? getDeploymentProfileGraphCoverage(profile) : undefined;
  const profileGraphWarning = profile ? getDeploymentProfileWarning(profile) : undefined;

  const handleSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();

    const result = await store.busy.perform(() => store.handleSubmit(accounts, pangolin.id));

    if (result.kind === ResultKind.OK) {
      history.replace(pagePathToPathString({
        kind: BasePath.ADD_PANGOLIN_SUCCESS,
        pangolinID: pangolin.id,
      }));
    }
  }, [pangolin, store, accounts, history]);

  const top = (
    <>
      <div className={styles.title}>
        {Messages.title()}
      </div>
      <div className={styles.instructions}>
        {Messages.instructions()}
      </div>
    </>
  );

  const middle = (
    <div className={styles.content}>
      <div className={styles.section}>
        <div className={styles.cardContainer}>
          <ProfileSelectInput
            styles={styles}
            choices={store.profileChoices}
            choice={DeploymentProfile.PASSIVE}
          />
          <ProfileSelectInput
            styles={styles}
            choices={store.profileChoices}
            choice={DeploymentProfile.ACTIVE_FULL}
          />
          <ProfileSelectInput
            styles={styles}
            choices={store.profileChoices}
            choice={DeploymentProfile.ACTIVE_INTERNET}
          />
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.detail}>
          <ReactMarkdown className={styles.detail}>
            {profileDetails}
          </ReactMarkdown>
        </div>
      </div>
      {profileGraphCoverage && (
        <div className={styles.graphContainer}>
          <img src={profileGraphImage} alt='Profile Graph' />
          <div className={styles.graphNotes}>
            {profileGraphGuide}
          </div>
          <table className={styles.graphFlowsTable}>
            <thead>
              <tr>
                <th>{Messages.profileGraphFlowsTableHeaderFlow()}</th>
                <th>{Messages.profileGraphFlowsTableHeaderLocation()}</th>
                <th>↔</th>
                <th>{Messages.profileGraphFlowsTableHeaderLocation()}</th>
                <th>{Messages.profileGraphFlowsTableHeaderCovered()}</th>
              </tr>
            </thead>
            <tbody>
              <tr className={classNames({ [styles.graphFlowDisabled]: !profileGraphCoverage[0] })}>
                <td><span style={{ backgroundColor: '#EF5350' }} className={styles.graphFlowLine} /></td>
                <td>{Messages.profileGraphFlowsTableDataLocationWifi()}</td>
                <td>↔</td>
                <td>{Messages.profileGraphFlowsTableDataLocationInternet()}</td>
                <td>{profileGraphCoverage[0] ? '✓' : '✕︀'}</td>
              </tr>
              <tr className={classNames({ [styles.graphFlowDisabled]: !profileGraphCoverage[1] })}>
                <td><span style={{ backgroundColor: '#E97937' }} className={styles.graphFlowLine} /></td>
                <td>{Messages.profileGraphFlowsTableDataLocationLan()}</td>
                <td>↔</td>
                <td>{Messages.profileGraphFlowsTableDataLocationInternet()}</td>
                <td>{profileGraphCoverage[1] ? '✓' : '✕︀'}</td>
              </tr>
              <tr className={classNames({ [styles.graphFlowDisabled]: !profileGraphCoverage[2] })}>
                <td><span style={{ backgroundColor: '#0087F5' }} className={styles.graphFlowLine} /></td>
                <td>{Messages.profileGraphFlowsTableDataLocationWifi()}</td>
                <td>↔</td>
                <td>{Messages.profileGraphFlowsTableDataLocationLan()}</td>
                <td>{profileGraphCoverage[2] ? '✓' : '✕︀'}</td>
              </tr>
              <tr className={classNames({ [styles.graphFlowDisabled]: !profileGraphCoverage[3] })}>
                <td><span style={{ backgroundColor: '#FFC107' }} className={styles.graphFlowLine} /></td>
                <td>{Messages.profileGraphFlowsTableDataLocationWifi()}</td>
                <td>↔</td>
                <td>{Messages.profileGraphFlowsTableDataLocationWifi()}</td>
                <td>{profileGraphCoverage[3] ? '✓' : '✕︀'}</td>
              </tr>
              <tr className={classNames({ [styles.graphFlowDisabled]: !profileGraphCoverage[4] })}>
                <td><span style={{ backgroundColor: '#73D277' }} className={styles.graphFlowLine} /></td>
                <td>{Messages.profileGraphFlowsTableDataLocationLan()}</td>
                <td>↔</td>
                <td>{Messages.profileGraphFlowsTableDataLocationLan()}</td>
                <td>{profileGraphCoverage[4] ? '✓' : '✕︀'}</td>
              </tr>
            </tbody>
          </table>
          <div className={styles.graphWarning}>
            {profileGraphWarning}
          </div>
        </div>
      )}
    </div>
  );

  const bottom = (
    <form onSubmit={handleSubmit}>
      <div className={styles.action}>
        <ActiveButton busy={store.busy} disabled={!profile} type='submit'>
          {Messages.next()}
        </ActiveButton>
      </div>
    </form>
  );

  return (
    <PartitionedPage top={top} middle={middle} bottom={bottom}/>
  );
});

export const AddPangolinProfile = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  return (
    <BaseAddPangolinProfile
      accounts={accounts}
      pangolin={pangolin}
    />
  );
});
